// Files (PLAN.md 4.6, brief 1A-7): what may be uploaded, how a name is cleaned, which record a
// file can belong to and who may attach or open it. Pure: no database, no storage, no clock.

import { reaches, type Access, type Area, type RecordKind } from "./permissions";
import type { Module } from "./modules";

/** The most one file may hold: 25 MB. */
export const MAX_FILE_BYTES = 25 * 1024 * 1024;
/**
 * Files travel in parts of at most 4 MB each way: a Netlify Function takes and gives at most
 * about 4.5 MB of binary per request, so a 25 MB file is sent and fetched in up to 7 parts.
 */
export const PART_BYTES = 4 * 1024 * 1024;

type Family = "document" | "image" | "font" | "audio";

interface FileType {
  mime: string;
  family: Family;
  /** Whether the first bytes look like this type. Text has no signature: it has no NUL bytes. */
  looks: (head: Uint8Array) => boolean;
}

const starts = (head: Uint8Array, bytes: number[], at = 0) =>
  bytes.every((b, i) => head[at + i] === b);
const ascii = (s: string) => [...s].map((c) => c.charCodeAt(0));
const zip = (h: Uint8Array) => starts(h, [0x50, 0x4b, 0x03, 0x04]);
const ole = (h: Uint8Array) => starts(h, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1]);
const text = (h: Uint8Array) => h.length > 0 && !h.includes(0);

/** Allowed files by extension. Anything else is refused. */
export const FILE_TYPES: Readonly<Record<string, FileType>> = {
  pdf: { mime: "application/pdf", family: "document", looks: (h) => starts(h, ascii("%PDF-")) },
  jpg: { mime: "image/jpeg", family: "image", looks: (h) => starts(h, [0xff, 0xd8, 0xff]) },
  jpeg: { mime: "image/jpeg", family: "image", looks: (h) => starts(h, [0xff, 0xd8, 0xff]) },
  png: {
    mime: "image/png",
    family: "image",
    looks: (h) => starts(h, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]),
  },
  heic: {
    mime: "image/heic",
    family: "image",
    looks: (h) =>
      starts(h, ascii("ftyp"), 4) &&
      ["heic", "heix", "hevc", "heim", "heis", "mif1", "msf1"].some((b) => starts(h, ascii(b), 8)),
  },
  webp: {
    mime: "image/webp",
    family: "image",
    looks: (h) => starts(h, ascii("RIFF")) && starts(h, ascii("WEBP"), 8),
  },
  doc: { mime: "application/msword", family: "document", looks: ole },
  docx: {
    mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    family: "document",
    looks: zip,
  },
  xls: { mime: "application/vnd.ms-excel", family: "document", looks: ole },
  xlsx: {
    mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    family: "document",
    looks: zip,
  },
  txt: { mime: "text/plain", family: "document", looks: text },
  csv: { mime: "text/csv", family: "document", looks: text },
  // A remittance file (an 835) is plain text (brief 1E-3).
  "835": { mime: "text/plain", family: "document", looks: text },
  edi: { mime: "text/plain", family: "document", looks: text },
  ttf: { mime: "font/ttf", family: "font", looks: (h) => starts(h, [0x00, 0x01, 0x00, 0x00]) },
  otf: { mime: "font/otf", family: "font", looks: (h) => starts(h, ascii("OTTO")) },
  woff2: { mime: "font/woff2", family: "font", looks: (h) => starts(h, ascii("wOF2")) },
  // A voicemail (brief 1B-6): kept only with its call's conversation, never uploaded by hand.
  mp3: {
    mime: "audio/mpeg",
    family: "audio",
    looks: (h) => starts(h, ascii("ID3")) || (h[0] === 0xff && ((h[1] ?? 0) & 0xe0) === 0xe0),
  },
};

/** Images a browser can show as a logo (HEIC can't be shown by most browsers). */
export const LOGO_EXTENSIONS = ["png", "jpg", "jpeg", "webp"] as const;
export const FONT_EXTENSIONS = ["woff2", "ttf", "otf"] as const;

/** The extension list for a file picker: everything, or only what this record takes. */
export function acceptFor(purpose: "any" | "logo" | "font"): string {
  if (purpose === "logo") return LOGO_EXTENSIONS.map((e) => `.${e}`).join(",");
  if (purpose === "font") return FONT_EXTENSIONS.map((e) => `.${e}`).join(",");
  return Object.keys(FILE_TYPES)
    .filter((e) => FILE_TYPES[e]?.family !== "font" && FILE_TYPES[e]?.family !== "audio")
    .map((e) => `.${e}`)
    .join(",");
}

export const TYPES_IN_WORDS = "PDF, JPG, PNG, HEIC, WebP, Word, Excel, plain text or CSV";

/** The extension of a name, lower-case, or "" when it has none. */
export function extensionOf(name: string): string {
  const m = /\.([A-Za-z0-9]{1,8})$/.exec(name.trim());
  return m ? (m[1] as string).toLowerCase() : "";
}

/** The type of a stored file by its MIME type, for downloads. */
export function typeByMime(mime: string): FileType | null {
  return Object.values(FILE_TYPES).find((t) => t.mime === mime) ?? null;
}

const NAME_MAX = 120;

/**
 * A file's display name, cleaned: only the last part of a path, no control characters or
 * characters that break a download, spaces collapsed, at most 120 characters with the extension
 * kept. The name is only ever shown; where the file is stored never comes from it.
 */
export function cleanName(raw: string): string {
  const last = String(raw).split(/[\\/]/).pop() ?? "";
  const ext = extensionOf(last);
  let name = last
    .replace(/[\p{Cc}\p{Cf}"<>:|?*%;]/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^[.\s]+|[.\s]+$/g, "");
  if (name.length > NAME_MAX) {
    const tail = ext ? `.${ext}` : "";
    name = `${name.slice(0, NAME_MAX - tail.length).trim()}${tail}`;
  }
  if (!name || name === ext) name = ext ? `file.${ext}` : "file";
  return name;
}

// ---- Records a file can belong to ----------------------------------------------------------

export interface FileRecord {
  /** The area whose View opens the file and whose Edit attaches one. */
  area: Area;
  /** The module the record belongs to; while it's off, its files are "not available". */
  module?: Module;
  /** Only an Admin may attach or open it (Settings › Branding is Admin only). */
  admin?: true;
  /** The kind of record grant that can list "files" for outside people. */
  grant?: RecordKind;
  /** What the record's history is kept under in audit_events. */
  historyKind: string;
}

export const FILE_RECORDS = {
  branding: { area: "settings", admin: true, historyKind: "tenant_branding" },
  patient: { area: "patients", module: "homecare", grant: "patient", historyKind: "patient" },
  visit: { area: "visits", module: "homecare", historyKind: "visit" },
  client: { area: "clients", historyKind: "client" },
  candidate: { area: "candidates", historyKind: "candidate" },
  worker: { area: "workers", module: "staffing", historyKind: "worker" },
  trip: { area: "trips", module: "transport", grant: "trip", historyKind: "trip" },
  vehicle: { area: "fleet", module: "transport", historyKind: "vehicle" },
  // A caregiver's or driver's documents (brief 1B-2); a person can be in several modules.
  person: { area: "workers", historyKind: "person" },
  // An email's or fax's attachments (brief 1B-3) belong to its conversation in the inbox.
  thread: { area: "inbox", historyKind: "thread" },
  // A PDF uploaded with Inbox › Outbox › Upload to mail (brief 1B-4); its record is the company.
  outbox: { area: "inbox", historyKind: "outbox" },
  // A remittance (835, EOB, check stub) belongs to its payment in Home Care › Payments (1E-3).
  payment: { area: "billing", module: "homecare", historyKind: "payment" },
  // The exported payroll file belongs to its run in Home Care › Payroll (brief 1E-4).
  payroll: { area: "payroll", module: "homecare", historyKind: "payroll_period" },
} as const satisfies Record<string, FileRecord>;
export type FileRecordKind = keyof typeof FILE_RECORDS;

export function isFileRecord(kind: string): kind is FileRecordKind {
  return Object.hasOwn(FILE_RECORDS, kind);
}

/**
 * Whether a person's roles reach a record's files at a level: View to open, Edit to attach or
 * archive. "Only their own" reaches the records the person is assigned to (each module brings
 * its assignments; until then nobody is assigned, so it reaches none).
 */
export function canReachFiles(
  access: Access,
  kind: FileRecordKind,
  level: "view" | "edit",
  assignedTo: readonly string[],
  userId: string,
): boolean {
  const rec: FileRecord = FILE_RECORDS[kind];
  if (rec.admin && !access.admin) return false;
  return reaches(access, rec.area, level, { assigned_to: assignedTo }, userId);
}

// ---- Reading an upload ---------------------------------------------------------------------

export interface UploadPlan {
  name: string;
  mime: string;
  size: number;
  parts: number;
  recordKind: FileRecordKind;
  recordId: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
export const isUuid = (v: unknown): v is string => typeof v === "string" && UUID.test(v);

const MB = 1024 * 1024;

/** How many parts a file of this size travels in. */
export const partsFor = (size: number) => Math.max(1, Math.ceil(size / PART_BYTES));

/** The length part n of a file must have. */
export function partLength(size: number, n: number): number {
  const parts = partsFor(size);
  if (n < 0 || n >= parts) return 0;
  return n < parts - 1 ? PART_BYTES : size - PART_BYTES * (parts - 1);
}

/**
 * Reads the start of an upload: {name, size, kind, id}. Refuses in plain words a file over
 * 25 MB, an empty file, a type not on the list, a font anywhere but Branding, and anything but
 * an image or a font on Branding.
 */
export function readUpload(
  input: unknown,
): { ok: true; plan: UploadPlan } | { ok: false; error: string } {
  if (typeof input !== "object" || input === null || Array.isArray(input))
    return { ok: false, error: "Pick a file to upload." };
  const b = input as Record<string, unknown>;
  if (typeof b.kind !== "string" || !isFileRecord(b.kind) || !isUuid(b.id))
    return { ok: false, error: "Say which record the file belongs to." };
  if (typeof b.name !== "string" || !b.name.trim())
    return { ok: false, error: "The file needs a name." };
  const size = b.size;
  if (typeof size !== "number" || !Number.isInteger(size) || size <= 0)
    return { ok: false, error: "That file is empty." };
  if (size > MAX_FILE_BYTES)
    return {
      ok: false,
      error: `That file is ${(size / MB).toFixed(1)} MB. Files can be up to 25 MB.`,
    };
  const name = cleanName(b.name);
  const ext = extensionOf(name);
  const type = FILE_TYPES[ext];
  if (!type)
    return {
      ok: false,
      error: `That kind of file can't be uploaded. Use ${TYPES_IN_WORDS}.`,
    };
  if (b.kind === "branding") {
    const logo = (LOGO_EXTENSIONS as readonly string[]).includes(ext);
    if (!logo && type.family !== "font")
      return {
        ok: false,
        error: "Branding takes a logo (PNG, JPG or WebP) or a font file (WOFF2, TTF or OTF).",
      };
  } else if (type.family === "font") {
    return { ok: false, error: "Font files can only be uploaded on Settings › Branding." };
  } else if (type.family === "audio" && b.kind !== "thread") {
    return { ok: false, error: `That kind of file can't be uploaded. Use ${TYPES_IN_WORDS}.` };
  }
  return {
    ok: true,
    plan: {
      name,
      mime: type.mime,
      size,
      parts: partsFor(size),
      recordKind: b.kind,
      recordId: b.id,
    },
  };
}

/** Whether the first bytes of an upload match the type its name says. Null when they do. */
export function refuseContent(mime: string, head: Uint8Array): string | null {
  const type = Object.entries(FILE_TYPES).find(([, t]) => t.mime === mime)?.[1];
  if (!type) return `That kind of file can't be uploaded. Use ${TYPES_IN_WORDS}.`;
  return type.looks(head)
    ? null
    : "That file isn't what its name says. Check the file and try again.";
}

/** Archiving a file needs a reason. Returns why it's refused, or null. */
export function refuseFileArchive(reason: unknown): string | null {
  return typeof reason === "string" && reason.trim() ? null : "Say why the file is archived.";
}

/** What a branding file can be used as. */
export function brandingUse(mime: string): "logo" | "font" | null {
  if (["image/png", "image/jpeg", "image/webp"].includes(mime)) return "logo";
  if (mime.startsWith("font/")) return "font";
  return null;
}
